/* ============================================================
   BORA Skin theme JS
   Modules: focus-trap → announcement → header → mega-menu →
            dialogs (mobile menu, search overlay) → carousel-snap →
            footer-accordion → predictive-search → hero-slider → hero-arc → category-tabs →
            language-switch
   Each module is a self-IIFE that no-ops without its DOM target.
   ============================================================ */


/* === bora-skin/js/focus-trap.js === */
// Traps Tab focus within a panel. Returns a release() function.
// Usage: const release = trapFocus(panelEl); ... release();
function trapFocus(panel) {
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function getFocusable() {
    return Array.from(panel.querySelectorAll(FOCUSABLE))
      .filter(el => !el.closest('[aria-hidden="true"], [hidden], [inert]') && el.getClientRects().length > 0);
  }
  function handler(e) {
    if (e.key !== 'Tab') return;
    const els = getFocusable();
    if (!els.length) { e.preventDefault(); return; }
    const first = els[0], last = els[els.length - 1];
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }
  panel.addEventListener('keydown', handler);
  return function release() { panel.removeEventListener('keydown', handler); };
}
if (window.BORA) window.BORA.trapFocus = trapFocus;


/* === announcement === */
// Cross-fades the server-rendered messages (snippets/announcement.liquid).
// Language comes from the Shopify locale (/ vs /en), so there is no
// client-side dictionary to fight the rendered text.
(function () {
  const STORAGE_KEY = 'bora.announcement.dismissed';
  const FADE_MS = 6000;

  const bar = document.getElementById('announcement');
  if (!bar) return;

  let dismissed = false;
  try { dismissed = localStorage.getItem(STORAGE_KEY) === '1'; } catch (e) {}
  if (dismissed) { bar.hidden = true; return; }

  const msgs = Array.from(bar.querySelectorAll('[data-announcement-msg]'));
  const close = bar.querySelector('.announcement__close');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let idx = 0;
  let paused = false;
  let timer = null;

  function cycle() {
    if (paused || msgs.length < 2) return;
    const current = msgs[idx];
    current.classList.add('is-fading');
    setTimeout(() => {
      current.hidden = true;
      current.classList.remove('is-fading');
      idx = (idx + 1) % msgs.length;
      msgs[idx].hidden = false;
    }, 400);
  }

  if (!reduceMotion) timer = setInterval(cycle, FADE_MS);

  bar.addEventListener('mouseenter', () => { paused = true; });
  bar.addEventListener('mouseleave', () => { paused = false; });
  bar.addEventListener('focusin', () => { paused = true; });
  bar.addEventListener('focusout', () => { paused = false; });

  if (close) close.addEventListener('click', () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
    bar.hidden = true;
    clearInterval(timer);
  });
})();

/* === bora-skin/js/header.js === */
// Sticky-compress header on scroll — spec §5.1
(function () {
  const header = document.getElementById('header');
  if (!header) return;

  const THRESHOLD = 60;
  let ticking = false;

  function update() {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > THRESHOLD);
    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(update);
      ticking = true;
    }
  }, { passive: true });

  update();
})();

/* === bora-skin/js/mega-menu.js === */
// Mega-menu. Mouse: hovering a nav link opens its panel (unchanged).
// Keyboard: the toggle button after each link (visually hidden until
// focused) opens the panel and moves focus into it; Tab past the last
// panel link continues to the next nav link; Shift+Tab from the first
// returns to the toggle; Esc closes; leaving the header closes.
(function () {
  const header = document.getElementById('header');
  const navLinks = document.querySelectorAll('[data-megamenu]');
  const toggles = document.querySelectorAll('[data-megamenu-toggle]');
  const panels = document.querySelectorAll('[data-megamenu-panel]');
  if (!navLinks.length) return;

  let openKey = null;
  let closeTimer = null;

  const panelFor = key => document.querySelector('[data-megamenu-panel="' + key + '"]');
  const toggleFor = key => document.querySelector('[data-megamenu-toggle="' + key + '"]');

  function open(key) {
    if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    if (openKey === key) return;
    // is-active keeps the link underlined while the pointer is inside its panel.
    navLinks.forEach(l => l.classList.toggle('is-active', l.dataset.megamenu === key));
    toggles.forEach(t => t.setAttribute('aria-expanded', t.dataset.megamenuToggle === key ? 'true' : 'false'));
    panels.forEach(p => {
      const isMatch = p.dataset.megamenuPanel === key;
      p.hidden = !isMatch;
      p.classList.toggle('is-open', isMatch);
    });
    openKey = key;
  }

  function close() {
    navLinks.forEach(l => l.classList.remove('is-active'));
    toggles.forEach(t => t.setAttribute('aria-expanded', 'false'));
    panels.forEach(p => { p.classList.remove('is-open'); p.hidden = true; });
    openKey = null;
  }

  function deferredClose() {
    if (closeTimer) clearTimeout(closeTimer);
    closeTimer = setTimeout(close, 160);
  }

  // Mouse
  navLinks.forEach(link => {
    link.addEventListener('mouseenter', () => open(link.dataset.megamenu));
    link.addEventListener('mouseleave', deferredClose);
    // Keyboard focus on a plain link closes any panel opened from a toggle.
    link.addEventListener('focus', () => { if (openKey) close(); });
  });
  panels.forEach(panel => {
    panel.addEventListener('mouseenter', () => {
      if (closeTimer) { clearTimeout(closeTimer); closeTimer = null; }
    });
    panel.addEventListener('mouseleave', deferredClose);
  });

  // Keyboard
  toggles.forEach(btn => {
    btn.addEventListener('click', () => {
      const key = btn.dataset.megamenuToggle;
      if (openKey === key) { close(); return; }
      open(key);
      const first = panelFor(key).querySelector('a[href]');
      if (first) first.focus();
    });
  });

  panels.forEach(panel => {
    panel.addEventListener('keydown', e => {
      if (e.key !== 'Tab') return;
      const links = panel.querySelectorAll('a[href]');
      const btn = toggleFor(panel.dataset.megamenuPanel);
      if (!links.length || !btn) return;
      if (!e.shiftKey && document.activeElement === links[links.length - 1]) {
        e.preventDefault();
        close();
        (btn.nextElementSibling || btn).focus();
      } else if (e.shiftKey && document.activeElement === links[0]) {
        e.preventDefault();
        btn.focus();
      }
    });
  });

  if (header) {
    header.addEventListener('focusout', e => {
      if (openKey && !header.contains(e.relatedTarget)) close();
    });
  }

  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape' || !openKey) return;
    const btn = toggleFor(openKey);
    const wasInside = panelFor(openKey).contains(document.activeElement);
    close();
    if (wasInside && btn) btn.focus();
  });
})();


/* === dialogs: mobile menu + search overlay === */
// Closed dialogs are `inert` so keyboard and screen-reader users can't land
// inside an off-screen panel (they're only hidden with transforms).
(function () {
  function setupDialog(opts) {
    const panel = document.getElementById(opts.id);
    const openBtn = document.getElementById(opts.openId);
    if (!panel) return;
    panel.inert = panel.getAttribute('aria-hidden') !== 'false';
    if (!openBtn) return;

    let lastFocus = null;
    let releaseTrap = null;

    function open() {
      lastFocus = document.activeElement;
      panel.inert = false;
      panel.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      if (opts.lockScroll) document.body.style.overflow = 'hidden';
      releaseTrap = trapFocus(panel);
      const target = opts.focus ? document.getElementById(opts.focus) : panel.querySelector('button, a, input');
      if (target) setTimeout(() => target.focus(), opts.focus ? 100 : 0);
    }
    function close() {
      if (panel.getAttribute('aria-hidden') !== 'false') return;
      panel.setAttribute('aria-hidden', 'true');
      panel.inert = true;
      openBtn.setAttribute('aria-expanded', 'false');
      if (opts.lockScroll) document.body.style.overflow = '';
      if (releaseTrap) { releaseTrap(); releaseTrap = null; }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    openBtn.setAttribute('aria-expanded', 'false');
    openBtn.setAttribute('aria-controls', opts.id);
    openBtn.addEventListener('click', open);
    opts.closeIds.forEach(id => {
      const el = document.getElementById(id);
      if (el) el.addEventListener('click', close);
    });
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && panel.getAttribute('aria-hidden') === 'false') close();
    });
  }

  setupDialog({ id: 'mobile-menu', openId: 'menu-open-btn', closeIds: ['menu-close-btn', 'mobile-menu-overlay'], lockScroll: true });
  setupDialog({ id: 'search-overlay', openId: 'search-open-btn', closeIds: ['search-close-btn', 'search-overlay-bg'], focus: 'search-overlay-input' });
})();

/* === bora-skin/js/carousel-snap.js === */
// Bestsellers carousel rows — per-row progress bar + desktop arrows.
// Each row uses native horizontal scroll-snap; arrows nudge by one
// card width at a time. Works the same way mobile (no arrows) and desktop.
(function () {
  const rows = document.querySelectorAll('[data-carousel-row]');
  rows.forEach(setupRow);

  function setupRow(row) {
    const track = row.querySelector('[data-carousel-track]');
    const progressBar = row.querySelector('[data-carousel-progress] > *');
    const prev = row.querySelector('[data-carousel-prev]');
    const next = row.querySelector('[data-carousel-next]');
    if (!track) return;

    function getStep() {
      const first = track.firstElementChild;
      if (!first) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 12;
      return first.getBoundingClientRect().width + gap;
    }

    function update() {
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 4) {
        if (progressBar) progressBar.parentElement.style.opacity = '0';
        if (prev) prev.setAttribute('disabled', '');
        if (next) next.setAttribute('disabled', '');
        return;
      }
      if (progressBar) {
        progressBar.parentElement.style.opacity = '';
        // Snap-aligned tracks sometimes can't reach scrollWidth - clientWidth
        // (the last card snaps to the start of the viewport), so derive the
        // effective end from the last card's right edge.
        const lastCard = track.lastElementChild;
        const effectiveMax = lastCard
          ? Math.max(1, lastCard.offsetLeft + lastCard.offsetWidth - track.clientWidth)
          : max;
        const ratio = Math.min(1, Math.max(0, track.scrollLeft / effectiveMax));
        // The bar is 25% wide of its parent. translateX percentages are
        // relative to the bar's own width — so 100% travel of the parent =
        // 400% of the bar. Travel range we want is 0%–75% of parent (the
        // bar's left edge moves from 0% to 75%, right edge from 25% to 100%).
        // 75% of parent / 25% bar width = 300% of bar.
        progressBar.style.transform = `translateX(${(300 * ratio).toFixed(2)}%)`;
      }
      if (prev) prev.toggleAttribute('disabled', track.scrollLeft <= 2);
      if (next) next.toggleAttribute('disabled', track.scrollLeft >= max - 2);
    }

    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);

    if (prev) prev.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });
    if (next) next.addEventListener('click', () => {
      track.scrollBy({ left: getStep(), behavior: 'smooth' });
    });

    // Initial state
    update();
  }
})();


/* === footer accordion === */
// Mobile footer columns collapse; headings become real toggle buttons (<1024px).
(function () {
  const cols = document.querySelectorAll('.footer__col[data-accordion]');
  if (!cols.length) return;
  const desktop = window.matchMedia('(min-width: 1024px)');

  cols.forEach(col => {
    const heading = col.querySelector('h3');
    if (!heading) return;

    function sync() {
      if (desktop.matches) {
        heading.removeAttribute('role');
        heading.removeAttribute('tabindex');
        heading.removeAttribute('aria-expanded');
      } else {
        heading.setAttribute('role', 'button');
        heading.setAttribute('tabindex', '0');
        heading.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
      }
    }
    function toggle() {
      if (desktop.matches) return;
      col.classList.toggle('is-open');
      heading.setAttribute('aria-expanded', col.classList.contains('is-open') ? 'true' : 'false');
    }

    heading.addEventListener('click', toggle);
    heading.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); }
    });
    if (desktop.addEventListener) desktop.addEventListener('change', sync);
    sync();
  });
})();

/* === predictive search === */
// Renders sections/predictive-search.liquid through the Section Rendering API
// (/search/suggest?section_id=predictive-search), so money format, stock state
// and translations match the rest of the store. Accessible combobox pattern.
(function () {
  const DEBOUNCE = 220;
  const MIN_CHARS = 2;
  const inputs = document.querySelectorAll('[data-predictive-input]');
  if (!inputs.length) return;

  const suggestUrl = (window.BORA && window.BORA.routes && window.BORA.routes.predictive_search_url) || '/search/suggest';
  const cache = new Map();

  inputs.forEach((input, n) => {
    const form = input.closest('form');
    const dropdown = form ? form.querySelector('[data-predictive-results]') : null;
    if (!dropdown) return;

    const listId = 'predictive-search-list-' + n;
    dropdown.id = listId;
    dropdown.setAttribute('role', 'listbox');
    input.setAttribute('role', 'combobox');
    input.setAttribute('aria-autocomplete', 'list');
    input.setAttribute('aria-expanded', 'false');
    input.setAttribute('aria-controls', listId);

    let controller = null;
    let timer = null;
    let activeIndex = -1;

    function options() { return Array.from(dropdown.querySelectorAll('[role="option"]')); }

    function show() {
      dropdown.hidden = false;
      input.setAttribute('aria-expanded', 'true');
    }
    function hide() {
      dropdown.hidden = true;
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    function paint(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const results = doc.getElementById('predictive-search-results');
      if (!results) { hide(); return; }
      dropdown.innerHTML = results.innerHTML;
      activeIndex = -1;
      show();
    }

    function search() {
      const q = input.value.trim();
      if (q.length < MIN_CHARS) { hide(); return; }
      if (cache.has(q)) { paint(cache.get(q)); return; }
      if (controller) controller.abort();
      controller = new AbortController();
      const url = suggestUrl + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection,query'
        + '&resources[limit]=6&resources[limit_scope]=each'
        + '&resources[options][unavailable_products]=last'
        + '&section_id=predictive-search';
      fetch(url, { signal: controller.signal, credentials: 'same-origin' })
        .then(r => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
        .then(html => {
          cache.set(q, html);
          // Ignore responses for a query the shopper has already moved past.
          if (input.value.trim() === q) paint(html);
        })
        .catch(err => { if (err.name !== 'AbortError') hide(); });
    }

    function setActive(i) {
      const opts = options();
      if (!opts.length) return;
      activeIndex = (i + opts.length) % opts.length;
      opts.forEach((el, k) => {
        const on = k === activeIndex;
        el.classList.toggle('is-active', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      input.setAttribute('aria-activedescendant', opts[activeIndex].id);
      opts[activeIndex].scrollIntoView({ block: 'nearest' });
    }

    input.addEventListener('input', () => {
      clearTimeout(timer);
      timer = setTimeout(search, DEBOUNCE);
    });
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_CHARS && dropdown.innerHTML.trim()) show();
    });
    input.addEventListener('keydown', e => {
      if (dropdown.hidden) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(activeIndex + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(activeIndex - 1); }
      else if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const opt = options()[activeIndex];
        if (opt) window.location.href = opt.href;
      } else if (e.key === 'Escape') { e.stopPropagation(); hide(); }
    });
    document.addEventListener('click', e => { if (!form.contains(e.target)) hide(); });
  });
})();

/* === hero slider === */
// sections/hero-slider.liquid. Cross-fading slides; inactive slides are
// `inert`. Autoplay pauses on hover / focus / hidden tab / off-screen and has
// a pause button (WCAG 2.2.2). No autoplay under prefers-reduced-motion.
(function () {
  const root = document.querySelector('[data-hero-slider]');
  if (!root) return;
  const slides = Array.from(root.querySelectorAll('.hs__slide'));
  if (slides.length < 2) return;
  const dots = Array.from(root.querySelectorAll('[data-hs-dot]'));
  const pauseBtn = root.querySelector('[data-hs-pause]');
  const interval = parseInt(root.dataset.interval, 10) || 7000;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const autoplay = root.dataset.autoplay === 'true' && !reduceMotion;

  let index = 0;
  let timer = null;
  let started = 0;
  // First rotation waits for the intro curtain when it's showing.
  let remaining = interval + (document.documentElement.classList.contains('intro-on') ? 1200 : 0);
  const holds = new Set(); // hover, focus, hidden, offscreen, user

  function running() { return autoplay && holds.size === 0; }

  function start() {
    if (!running()) return;
    root.classList.add('is-playing');
    root.classList.remove('is-paused');
    started = Date.now();
    clearTimeout(timer);
    timer = setTimeout(() => go(index + 1), Math.max(0, remaining));
  }
  function hold(reason) {
    const wasRunning = running();
    holds.add(reason);
    if (!wasRunning) return;
    clearTimeout(timer);
    remaining -= Date.now() - started;
    root.classList.add('is-paused');
  }
  function release(reason) {
    if (!holds.delete(reason)) return;
    start();
  }
  function restart() {
    clearTimeout(timer);
    remaining = interval;
    root.classList.remove('is-playing', 'is-paused');
    void root.offsetWidth; // restart the dot progress animation
    start();
  }

  function go(i) {
    i = (i + slides.length) % slides.length;
    if (i === index) return;
    root.classList.add('hs--moved');
    slides[index].classList.remove('is-active');
    slides[index].inert = true;
    slides[i].classList.add('is-active');
    slides[i].inert = false;
    dots.forEach((d, k) => {
      d.classList.toggle('is-active', k === i);
      if (k === i) d.setAttribute('aria-current', 'true');
      else d.removeAttribute('aria-current');
    });
    index = i;
    restart();
  }

  root.querySelector('[data-hs-prev]').addEventListener('click', () => go(index - 1));
  root.querySelector('[data-hs-next]').addEventListener('click', () => go(index + 1));
  dots.forEach((d, k) => d.addEventListener('click', () => go(k)));

  if (pauseBtn) {
    const icon = pauseBtn.querySelector('.material-symbols-outlined');
    pauseBtn.addEventListener('click', () => {
      const paused = holds.has('user');
      if (paused) {
        holds.delete('user');
        restart();
      } else {
        hold('user');
        root.classList.remove('is-playing', 'is-paused');
      }
      pauseBtn.setAttribute('aria-label', paused ? pauseBtn.dataset.labelPause : pauseBtn.dataset.labelPlay);
      if (icon) icon.textContent = paused ? 'pause' : 'play_arrow';
    });
  }

  root.addEventListener('mouseenter', () => hold('hover'));
  root.addEventListener('mouseleave', () => release('hover'));
  root.addEventListener('focusin', () => hold('focus'));
  root.addEventListener('focusout', e => { if (!root.contains(e.relatedTarget)) release('focus'); });
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) hold('hidden'); else release('hidden');
  });
  if ('IntersectionObserver' in window) {
    new IntersectionObserver(([e]) => {
      if (e.isIntersecting) release('offscreen'); else hold('offscreen');
    }).observe(root);
  }

  root.addEventListener('keydown', e => {
    if (e.key === 'ArrowLeft') { e.preventDefault(); go(index - 1); }
    else if (e.key === 'ArrowRight') { e.preventDefault(); go(index + 1); }
  });

  // Touch swipe (horizontal only).
  let sx = 0, sy = 0;
  root.addEventListener('touchstart', e => { sx = e.touches[0].clientX; sy = e.touches[0].clientY; }, { passive: true });
  root.addEventListener('touchend', e => {
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - sy;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) go(index + (dx < 0 ? 1 : -1));
  }, { passive: true });

  start();
})();

/* === hero arc === */
// sections/hero-slider.liquid — in-stock products on a curved 3D arc. Geometry
// is a port of the Framer "CurvedGalleryArc": cards sit on a circle (perspective
// + translateZ + rotateY), the ring loops forever, drifts slowly and can be
// dragged with inertia. A click on a card still opens the product; a drag does
// not. Drift pauses on hover / focus / hidden tab / off-screen / slide not
// shown, with a pause button (WCAG 2.2.2). Reduced motion: drag only.
(function () {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const RING_MIN = 14;      // cards in the ring (originals are cloned up to this)
  const HALF = 7;           // cards drawn each side of the centre
  const INERTIA = 0.75, DRAG = 1;
  const PERSP = 1200;       // px, must match the stage perspective

  document.querySelectorAll('[data-hs-arc]').forEach(root => {
    const arc = root.querySelector('.hs__arc');
    const stage = root.querySelector('.hs__arc-stage');
    const originals = Array.from(stage.querySelectorAll('.hs__arc-card'));
    if (originals.length < 5) return;

    const cards = originals.slice();
    while (cards.length < RING_MIN) {
      originals.forEach(c => {
        const k = c.cloneNode(true);
        k.setAttribute('aria-hidden', 'true');
        k.tabIndex = -1;
        const img = k.querySelector('img');
        if (img) { img.loading = 'lazy'; img.removeAttribute('fetchpriority'); }
        stage.appendChild(k);
        cards.push(k);
      });
    }
    const N = cards.length;
    stage.style.perspective = PERSP + 'px';

    // Names and prices live in a flat layer above the arc: laid out at their
    // projected size, so the text is rasterised crisp instead of stretched.
    const labelLayer = document.createElement('div');
    labelLayer.className = 'hs__arc-labels';
    labelLayer.setAttribute('aria-hidden', 'true');
    arc.appendChild(labelLayer);
    const labels = cards.map(c => {
      const l = document.createElement('div');
      l.className = 'hs__arc-label';
      const body = c.querySelector('.hs__arc-body');
      if (body) l.innerHTML = body.innerHTML;
      labelLayer.appendChild(l);
      return l;
    });

    const curve = Math.max(0, parseFloat(root.dataset.curve) || 0);
    const autoplay = root.dataset.autoplay === 'true' && !reduceMotion;
    const autoSpeed = (parseFloat(root.dataset.speed) || 4) * 0.05; // cards / second
    const slide = root.closest('.hs__slide');

    let span = 160, step = 0, radius = 0, centre = 0, cardW = 150, imgH = 190, midX = 0, midY = 0;
    function measure() {
      cardW = cards[0].offsetWidth || 150;
      imgH = cards[0].offsetHeight || cardW * 1.25;
      midX = stage.clientWidth / 2;
      midY = stage.clientHeight * 0.42;   // matches the card top in CSS
      span = cardW * 1.12;
      const strength = Math.min(1, curve / 300);
      step = strength === 0 ? 0 : 0.04 + strength * 0.2;
      if (strength > 0) {
        // strong curves blend towards wrapping almost all the way round
        const blend = Math.pow(strength, 2.2);
        step = (HALF * step * (1 - blend) + Math.PI * 2 * 0.98 * blend) / HALF;
      }
      radius = step === 0 ? 0 : span / step;
      centre = (1 + Math.cos(HALF * step)) / 2;
    }

    let offset = 0, velocity = 0, target = null, dragging = false;
    const holds = new Set();   // slow the drift: hover, focus, user
    const gates = new Set();   // stop drawing: hidden, offscreen, slide
    let raf = 0, last = 0;

    function wrap(i) {
      let rel = (i - offset) % N;
      if (rel < -N / 2) rel += N; else if (rel >= N / 2) rel -= N;
      return rel;
    }

    function layout() {
      for (let i = 0; i < N; i++) {
        const rel = wrap(i);
        const near = Math.abs(rel), c = cards[i];
        if (near > HALF + 0.6) { c.style.opacity = '0'; c.style.pointerEvents = 'none'; labels[i].style.opacity = '0'; continue; }
        let x, z = 0, rot = 0, th = 0;
        if (step === 0) {
          x = rel * span;
        } else {
          th = rel * step;
          x = Math.sin(th) * radius;
          z = (Math.cos(th) - centre) * radius;
          rot = th * 180 / Math.PI;
        }
        c.style.transform = 'translate3d(-50%,-50%,0) translateX(' + x.toFixed(1) + 'px) translateZ(' + z.toFixed(1) + 'px) rotateY(' + rot.toFixed(2) + 'deg)';
        c.style.opacity = (1 - Math.min(1, near / HALF) * 0.35).toFixed(3);
        c.style.pointerEvents = '';
        c.style.zIndex = String(1000 - Math.round(Math.abs(th) * 100) - Math.round(near));
        // the centre card and its two neighbours show name and price
        const l = labels[i];
        const a = Math.max(0, Math.min(1, (1.7 - near) / 0.7));
        l.style.opacity = a.toFixed(3);
        if (a > 0) {
          const s = PERSP / (PERSP - z);   // perspective scale of this card
          const w = cardW * s;
          l.style.setProperty('--s', (1 + (s - 1) * 0.55).toFixed(3));   // text grows less than the photo
          l.style.width = w.toFixed(1) + 'px';
          l.style.transform = 'translate(' + (midX + x * s - w / 2).toFixed(1) + 'px,' + (midY + (imgH / 2 + 9) * s).toFixed(1) + 'px)';
          l.style.zIndex = c.style.zIndex;
        }
      }
    }

    function drift() { return autoplay && holds.size === 0 ? autoSpeed : 0; }
    function idle() { return !dragging && target === null && Math.abs(velocity) < 0.0005 && drift() === 0; }

    function frame(t) {
      raf = 0;
      if (gates.size) { last = 0; return; }
      const dt = last ? Math.max(0.001, Math.min(0.05, (t - last) / 1000)) : 0.016;
      last = t;
      if (!dragging) {
        if (target !== null) {
          const d = target - offset;
          offset += d * (1 - Math.exp(-6 * dt));
          velocity = 0;
          if (Math.abs(d) < 0.002) { offset = target; target = null; }
        } else {
          velocity *= Math.pow(0.82 + INERTIA * 0.17, dt * 60);
          velocity += (drift() - velocity) * (1 - Math.exp(-1.2 * dt));
          offset += velocity * dt;
        }
      }
      layout();
      if (idle()) { last = 0; return; }
      raf = requestAnimationFrame(frame);
    }
    function kick() { if (!raf && !gates.size) raf = requestAnimationFrame(frame); }

    function hold(r) { holds.add(r); }
    function release(r) { if (holds.delete(r)) kick(); }
    function gate(r) { gates.add(r); }
    function ungate(r) { if (gates.delete(r)) { last = 0; kick(); } }

    // ---- drag (a tap stays a click; a drag never opens a product) ----
    let down = null, samples = [], suppress = false;
    function releaseVelocity() {
      const now = performance.now();
      while (samples.length > 2 && now - samples[0].t > 120) samples.shift();
      if (samples.length < 2) return 0;
      const a = samples[0], b = samples[samples.length - 1];
      const dt = (b.t - a.t) / 1000;
      if (dt <= 0 || now - b.t > 90) return 0;
      return -((b.x - a.x) / dt * DRAG) / span * 0.95;
    }
    arc.addEventListener('pointerdown', e => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = { id: e.pointerId, startX: e.clientX, lastX: e.clientX, moved: false };
      samples = [{ x: e.clientX, t: performance.now() }];
    });
    arc.addEventListener('pointermove', e => {
      if (!down || down.id !== e.pointerId) return;
      if (!down.moved) {
        if (Math.abs(e.clientX - down.startX) < 6) return;
        down.moved = true; dragging = true; target = null;
        arc.setPointerCapture(e.pointerId);
        arc.classList.add('is-dragging', 'is-moved');
      }
      offset += -(e.clientX - down.lastX) / span * DRAG;
      down.lastX = e.clientX;
      samples.push({ x: e.clientX, t: performance.now() });
      if (samples.length > 8) samples.shift();
      velocity = releaseVelocity();
      layout();
    });
    function endDrag(e, cancelled) {
      if (!down || down.id !== e.pointerId) return;
      const moved = down.moved;
      down = null;
      if (!moved) return;
      dragging = false;
      velocity = cancelled ? 0 : releaseVelocity();
      arc.classList.remove('is-dragging');
      if (!cancelled) { suppress = true; setTimeout(() => { suppress = false; }, 0); }
      if (arc.hasPointerCapture(e.pointerId)) arc.releasePointerCapture(e.pointerId);
      kick();
    }
    arc.addEventListener('pointerup', e => endDrag(e, false));
    arc.addEventListener('pointercancel', e => endDrag(e, true));
    arc.addEventListener('click', e => { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
    arc.addEventListener('dragstart', e => e.preventDefault());

    // ---- buttons, keys, focus ----
    function nudge(dir) {
      target = (target !== null ? target : Math.round(offset)) + dir * 2;
      arc.classList.add('is-moved');
      kick();
    }
    root.querySelector('[data-ha-prev]').addEventListener('click', () => nudge(-1));
    root.querySelector('[data-ha-next]').addEventListener('click', () => nudge(1));

    const pauseBtn = root.querySelector('[data-ha-pause]');
    if (pauseBtn) {
      const icon = pauseBtn.querySelector('.material-symbols-outlined');
      pauseBtn.addEventListener('click', () => {
        const paused = holds.has('user');
        if (paused) release('user'); else hold('user');
        pauseBtn.setAttribute('aria-label', paused ? pauseBtn.dataset.labelPause : pauseBtn.dataset.labelPlay);
        if (icon) icon.textContent = paused ? 'pause' : 'play_arrow';
      });
    }

    root.addEventListener('mouseenter', () => hold('hover'));
    root.addEventListener('mouseleave', () => release('hover'));
    root.addEventListener('focusin', e => {
      hold('focus');
      // keyboard focus brings that card to the centre (a mouse click must not move it)
      const i = cards.indexOf(e.target);
      if (i >= 0 && e.target.matches(':focus-visible')) {
        target = offset + wrap(i);
        kick();
      }
    });
    root.addEventListener('focusout', e => { if (!root.contains(e.relatedTarget)) release('focus'); });
    root.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); e.stopPropagation(); nudge(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); e.stopPropagation(); nudge(1); }
    });
    // a swipe here must not also swipe the campaign slider around it
    root.addEventListener('touchstart', e => e.stopPropagation(), { passive: true });
    root.addEventListener('touchend', e => e.stopPropagation(), { passive: true });

    // ---- only draw while it can be seen ----
    if (document.hidden) gate('hidden');
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) gate('hidden'); else ungate('hidden');
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        if (e.isIntersecting) ungate('offscreen'); else gate('offscreen');
      }).observe(root);
    }
    if (slide) {
      const sync = () => {
        if (slide.classList.contains('is-active')) ungate('slide'); else gate('slide');
      };
      sync();
      new MutationObserver(sync).observe(slide, { attributes: true, attributeFilter: ['class'] });
    }
    if ('ResizeObserver' in window) {
      new ResizeObserver(() => { measure(); layout(); }).observe(arc);
    }

    arc.classList.add('is-ready');
    measure();   // width is read after is-ready: cards are absolute from here
    layout();
    kick();
  });
})();

/* === category tabs === */
// sections/categories.liquid — WAI-ARIA tabs (automatic activation). Panels
// are server-rendered; switching just toggles `hidden`, then nudges the
// carousel-snap rows (they measured 0 while hidden) via a resize event.
(function () {
  document.querySelectorAll('[data-cat-tabs]').forEach(list => {
    const tabs = Array.from(list.querySelectorAll('[role="tab"]'));
    const panelOf = tab => document.getElementById(tab.getAttribute('aria-controls'));

    function select(tab, focus) {
      tabs.forEach(t => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const panel = panelOf(t);
        if (panel) panel.hidden = !on;
      });
      const panel = panelOf(tab);
      if (panel) {
        const track = panel.querySelector('[data-carousel-track]');
        if (track) track.scrollLeft = 0;
        panel.classList.remove('is-entering');
        void panel.offsetWidth;
        panel.classList.add('is-entering');
        setTimeout(() => panel.classList.remove('is-entering'), 800);
      }
      window.dispatchEvent(new Event('resize'));
      if (focus) tab.focus();
      // keep the active tab visible in the sideways-scrolling row (phones)
      const l = tab.offsetLeft - list.offsetLeft, r = l + tab.offsetWidth;
      if (l < list.scrollLeft || r > list.scrollLeft + list.clientWidth) {
        list.scrollTo({ left: l - 16, behavior: 'smooth' });
      }
    }

    tabs.forEach(t => t.addEventListener('click', () => select(t, false)));
    list.addEventListener('keydown', e => {
      const i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      let j = null;
      if (e.key === 'ArrowRight') j = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft') j = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') j = 0;
      else if (e.key === 'End') j = tabs.length - 1;
      if (j === null) return;
      e.preventDefault();
      select(tabs[j], true);
    });
  });
})();

/* === bora-skin/js/language-switch.js === */
// Shopify fills the switch's return_to with the URL the page loaded at.
// Collection/search filters, sort and pagination change the URL later via
// pushState, so refresh return_to on submit or SQ/EN would drop them.
document.addEventListener('submit', e => {
  const form = e.target;
  if (!form.classList || !form.classList.contains('lang-switch')) return;
  const returnTo = form.querySelector('input[name="return_to"]');
  if (returnTo) returnTo.value = location.pathname + location.search;
});
